import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { useTranslation } from "react-i18next";
import { formatMoneyCompact } from "@/lib/format";

interface DestinationData {
  destinationId: string;
  destination: string;
  revenue: number;
  percentage: number;
}

interface DestinationPieChartProps {
  data: DestinationData[];
}

const COLORS = [
  "#3b82f6",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#be016d",
  "#14b8a6",
  "#f97316",
  "#6366f1",
  "#84cc16",
];

export default function DestinationPieChart({ data }: DestinationPieChartProps) {
  const { t } = useTranslation();
  const chartData = data.map((d, idx) => ({
    name: d.destination,
    value: d.revenue,
    percentage: d.percentage,
    color: COLORS[idx % COLORS.length],
  }));

  return (
    <div className="card">
      <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
        {t("system.analytics.revenue.byDestination")}
      </h3>
      {chartData.length > 0 ? (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percentage }) => `${name} (${percentage}%)`}
                labelLine={false}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => [
                  formatMoneyCompact(value),
                  t("system.analytics.revenue.tooltipRevenue"),
                ]}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="py-12 text-center text-gray-500">{t("system.analytics.noData")}</p>
      )}

      {/* Legend as list */}
      <div className="mt-4 space-y-2">
        {chartData.slice(0, 5).map((item, idx) => (
          <div key={idx} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
              <span className="text-sm text-gray-700 dark:text-gray-300">{item.name}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-gray-900 dark:text-white">
                {formatMoneyCompact(item.value)}
              </span>
              <span className="ml-2 text-gray-500">({item.percentage}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
